import { StateCreator } from 'zustand'
import { SharedSlice } from '../../../../slice'
import { ILeaveType, IStaffMemberDetail, StaffRole } from '$models'
import { exceptionMessageHandler } from '$utils'
import {
  leaveTypeService,
  staffMemberService,
  staffRoleService,
} from '$services'

interface LeaveTypesState {
  leaveTypes: ILeaveType[]
  staffMembers: IStaffMemberDetail[]
  staffRoles: StaffRole[]
  showDeactivated: boolean
}

export interface LeaveTypesSlice extends LeaveTypesState {
  setShowDeactivated: () => void

  /**
   * Get Leave Types list
   */
  getLeaveTypes: () => Promise<void>

  /**
   * Get Staff Members list
   * @param includeDeleted
   */
  getStaffMembers: (includeDeleted?: boolean) => Promise<void>

  /**
   * Get Staff Roles
   * @param includeDeleted
   */
  getStaffRoles: (includeDeleted?: boolean) => Promise<void>

  /**
   * Resets LeaveTypes Slice state
   */
  resetLeaveTypesSlice: () => void
}

const initialState: LeaveTypesState = {
  leaveTypes: [],
  staffMembers: [],
  staffRoles: [],
  showDeactivated: false,
}

const createLeaveTypesSlice: StateCreator<
  LeaveTypesSlice & SharedSlice,
  [],
  [],
  LeaveTypesSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  setShowDeactivated: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),
  resetLeaveTypesSlice: () => set(initialState),

  // State API calls
  getLeaveTypes: async (): Promise<void> => {
    return leaveTypeService
      .getAll()
      .then((result) => {
        set(() => ({ leaveTypes: result }))
      })
      .catch((error) => {
        set(() => ({
          leaveTypes: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve leave types')
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  getStaffMembers: async (includeDeleted: boolean = true): Promise<void> => {
    return staffMemberService
      .getAll(undefined, includeDeleted, false)
      .then((result) => set({ staffMembers: result }))
      .catch((error) => {
        set({
          staffMembers: [],
        })
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
      .finally(() => {
        set({ isLoading: false })
      })
  },

  getStaffRoles: async (includeDeleted: boolean = true): Promise<void> => {
    return staffRoleService
      .getAll(includeDeleted)
      .then((result) => set({ staffRoles: result }))
      .catch((error) => {
        set({
          staffRoles: [],
        })
        exceptionMessageHandler(error, 'Failed to retrieve staff roles')
      })
  },
})

export default createLeaveTypesSlice
